import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { Trace } from '@engine/models'

import { ActivityTimeline } from '#components/ActivityTimeline'
import { AdminListLayout } from '#components/AdminListLayout'
import { KeyValueDisplay } from '#components/KeyValueDisplay.js'
import { Slider } from '#components/Slider.js'
import { Table } from '#components/Table'
import { useFetch } from '#hooks/useAdminResource'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import {
  UnmappedErrorsFilters,
  useUnmappedErrorsFilters,
} from './UnmappedErrorsFilters'

const columnHelper = createColumnHelper<Trace>()

const getErrorDetails = (trace: Trace) => {
  if (!trace?.transactions?.length) {
    return 'No transactions found'
  }

  for (const t of trace.transactions) {
    const response = t.response as any

    // Check for ebol error
    if (
      trace.category === 'ebol' &&
      response?.status === 'error' &&
      response?.errors?.some(e => e.type === 'unknown')
    ) {
      return `${response?.errors?.find(e => e.type === 'unknown')?.message || 'Unknown Error'}`
    }

    // Check for dispatching error
    if (trace.category === 'dispatching' && response?.status === 'error') {
      return `${response?.error || 'Internal Pickup Error'}`
    }
  }

  return 'Unknown'
}

const categoryBadge = (category: Trace['category']) => {
  const badgeClass =
    category === 'dispatching' ? 'badge-primary' : 'badge-secondary'

  return (
    <span className={`badge ${badgeClass} badge-sm uppercase`}>
      {category === 'dispatching' ? 'Tender' : 'EBOL'}
    </span>
  )
}

const UnmappedErrorsReport = () => {
  const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null)

  const { filters, localFilters, setLocalFilters } = useUnmappedErrorsFilters()

  // Fetch counts
  const { data: counts } = useFetch<{
    ebol: number
    tender: number
    total: number
  }>('unmapped-errors/counts', filters)

  const {
    items: traces,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<Trace>('unmapped-errors', {
    limit: 50,
    ...filters,
  })

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  const columns = useMemo(
    () => [
      columnHelper.accessor('targetId', {
        header: 'Target ID',
        cell: info => (
          <span
            className="font-mono text-xs text-blue-500 cursor-pointer hover:underline"
            onClick={() => setSelectedTrace(info.row.original)}
          >
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('timeStamp', {
        header: 'Timestamp',
        cell: info => (
          <span className="text-xs">
            {new Date(info.getValue()).toLocaleString()}
          </span>
        ),
      }),
      columnHelper.accessor(row => row.carrier?.code, {
        id: 'scac',
        header: 'SCAC',
        cell: info => (
          <span className="font-mono text-xs uppercase">
            {info.getValue() || '—'}
          </span>
        ),
      }),
      columnHelper.accessor('category', {
        header: 'Category',
        cell: info => categoryBadge(info.getValue()),
      }),
      columnHelper.display({
        header: 'Error Details',
        cell: info => (
          <span className="text-xs text-error">
            {getErrorDetails(info.row.original)}
          </span>
        ),
      }),
    ],
    [],
  )

  return (
    <AdminListLayout
      title="Unmapped Errors"
      isLoading={isLoading}
      error={error}
      onRefresh={refresh}
      filters={
        <>
          {counts && (
            <div className="mb-3 flex gap-4 items-center text-sm">
              <span className="text-base-content/70">
                Total: <span className="font-semibold">{counts.total}</span>
              </span>
              <span className="text-base-content/70">
                EBOL:{' '}
                <span className="font-semibold text-secondary">
                  {counts.ebol}
                </span>
              </span>
              <span className="text-base-content/70">
                Tender:{' '}
                <span className="font-semibold text-primary">
                  {counts.tender}
                </span>
              </span>
            </div>
          )}
          <UnmappedErrorsFilters
            isLoading={isLoading}
            onRefresh={refresh}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
          />
        </>
      }
      emptyMessage="No unmapped errors found"
      listContent={
        traces.length > 0 ? (
          <div className="h-full">
            <Table
              data={traces}
              columns={columns}
              getRowId={trace => trace.traceId}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
            <Slider
              isOpen={!!selectedTrace}
              onClose={() => setSelectedTrace(null)}
              title={
                selectedTrace?.targetId
                  ? `Trace Details: ${selectedTrace.targetId}`
                  : 'Trace Details'
              }
              width="60vw"
            >
              <KeyValueDisplay
                items={[
                  {
                    label: 'Target ID',
                    value: selectedTrace?.targetId,
                  },
                  {
                    label: 'Timestamp',
                    value: selectedTrace?.timeStamp,
                  },
                  {
                    label: 'SCAC',
                    value: selectedTrace?.carrier?.code,
                  },
                  {
                    label: 'Category',
                    value: selectedTrace?.category,
                  },
                  {
                    label: 'Error Details',
                    value: getErrorDetails(selectedTrace),
                  },
                ]}
              />
              <div className="p-4 overflow-auto h-full">
                {selectedTrace?.targetId ? (
                  <ActivityTimeline externalId={selectedTrace.targetId} />
                ) : (
                  <pre className="text-xs bg-base-200 p-4 rounded-lg overflow-auto">
                    {JSON.stringify(selectedTrace, null, 2)}
                  </pre>
                )}
              </div>
            </Slider>
          </div>
        ) : null
      }
    />
  )
}

export { UnmappedErrorsReport }
